import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// Styled Components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const TableWrapper = styled.div`
  max-height: 620px; /* roughly 10 rows */
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  font-size: 0.7rem;

   th, td {
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background-color: #87ceeb;
    font-weight: 700; /* explicitly bolder */
    color: #333;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }

  .positive {
    background-color: #e7f9ed;
    color: #2e7d32;
    font-weight: 500;
  }

  .negative {
    background-color: #ffeaea;
    color: #c62828;
    font-weight: 500;
  }

  .bold {
    font-weight: bold;
  }

  .highlight-top {
    background-color: rgba(0, 128, 0, 0.1);
    font-weight: bold;
  }

  .highlight-low {
    background-color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
  }

  .highlight-neutral {
    background-color: #f0f0f0;
  }

  .rank-up {
    color: green;
    font-weight: bold;
  }

  .rank-down {
    color: red;
    font-weight: bold;
  }

  .rank-neutral {
    color: #666;
  }
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const formatNumber = (value) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const RevenueByCountry = ({ salesData = [] }) => {
  const [countryData, setCountryData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("00");
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const years = [...new Set(salesData.map((row) => parseInt(row.Rok)))].sort((a, b) => b - a);
    setAvailableYears(years);
    setSelectedYear((prev) => prev ?? years[0]);
  }, [salesData]);

  useEffect(() => {
    if (!selectedYear) return;

    const includeFullYear = selectedMonth === "00";
    const currentMonth = parseInt(selectedMonth);
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? selectedYear - 1 : selectedYear;
    const yearAgo = selectedYear - 1;

    const computeRevenue = (row) =>
      parseFloat(row.K_15) || parseFloat(row.K_19) || parseFloat(row.K_10) ||
      parseFloat(row.K_21) || parseFloat(row.K_17) || parseFloat(row.K_11) || parseFloat(row.K_22) || 0;

    const countries = {};

    salesData.forEach((row) => {
      const rok = parseInt(row.Rok);
      const miesiac = parseInt(row.Miesiac);
      const country = row.KodKrajuNadaniaTIN || "Nieznany";
      const revenue = computeRevenue(row);
      if (!countries[country]) countries[country] = [];
      countries[country].push({ rok, miesiac, revenue });
    });

    const formatted = Object.entries(countries).map(([country, rows]) => {
      const current = rows.filter(
        (r) => r.rok === selectedYear && (includeFullYear || r.miesiac === currentMonth)
      ).reduce((sum, r) => sum + r.revenue, 0);

      const prev = includeFullYear ? 0 :
        rows.filter((r) => r.rok === prevYear && r.miesiac === prevMonth)
          .reduce((sum, r) => sum + r.revenue, 0);

      const yoy = rows.filter(
        (r) => r.rok === yearAgo && (includeFullYear || r.miesiac === currentMonth)
      ).reduce((sum, r) => sum + r.revenue, 0);

      const changeMM = includeFullYear || prev === 0 ? null : ((current - prev) / prev) * 100;
      const changeYY = yoy === 0 ? null : ((current - yoy) / yoy) * 100;

      return { kraj: country, current, prev, yoy, changeMM, changeYY };
    }).filter(r => r.kraj !== "Nieznany");

    // Rankings
    const rankedNow = [...formatted].sort((a, b) => b.current - a.current)
      .map((r, i) => ({ ...r, rank: i + 1 }));

    const rankedPrev = [...formatted].sort((a, b) => b.yoy - a.yoy)
      .map((r, i) => ({ kraj: r.kraj, prevRank: i + 1 }));

    const finalData = rankedNow.map((r) => {
      const prev = rankedPrev.find(p => p.kraj === r.kraj)?.prevRank ?? null;
      const rankChange = prev && r.rank ? prev - r.rank : null;
      return { ...r, prevRank: prev, rankChange };
    });

    setCountryData(finalData);
  }, [salesData, selectedYear, selectedMonth]);

  const exportToExcel = () => {
    const rows = countryData.map((r) => ({
      Kraj: r.kraj,
      "Wartość (PLN)": r.current.toFixed(2),
      "m/m-1": r.prev.toFixed(2),
      "% m/m-1": r.changeMM == null ? "—" : `${r.changeMM.toFixed(2)}%`,
      "r/r-1": r.yoy.toFixed(2),
      "% r/r-1": r.changeYY == null ? "—" : `${r.changeYY.toFixed(2)}%`,
      "Ranking": r.rank || "-",
      "Ranking r-1": r.prevRank || "-",
      "Zmiana rankingu": r.rankChange !== null 
        ? (r.rankChange > 0 
          ? `↑ ${r.rankChange}` 
          : r.rankChange < 0 
            ? `↓ ${Math.abs(r.rankChange)}` 
            : "–") : "-"
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kraje");
    XLSX.writeFile(wb, "dochody_kraje.xlsx");
  };

  const months = [
    ["00", "Cały rok"], ["01", "Styczeń"], ["02", "Luty"], ["03", "Marzec"],
    ["04", "Kwiecień"], ["05", "Maj"], ["06", "Czerwiec"], ["07", "Lipiec"],
    ["08", "Sierpień"], ["09", "Wrzesień"], ["10", "Październik"], ["11", "Listopad"], ["12", "Grudzień"]
  ];

  return (
    <Wrapper>
      <ChartTitle>
        Przychody według krajów
        <InfoIconWrapper data-tooltip-id="revenue-country-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="revenue-country-tooltip"
        place="left"
        content={
          <>
            <strong>Ranking krajów</strong><br/>
            Tabela pokazuje kraje wg wartości sprzedaży,
            zmiany m/m, r/r oraz pozycje w rankingu.
          </>
        }
      />

      <Controls>
        <Select value={selectedYear ?? ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
          <option value="">Rok</option>
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          {months.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
        </Select>
        <ExportButton onClick={exportToExcel}>
          <FaFileExcel /> Eksportuj do Excel
        </ExportButton>
      </Controls>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Kraj</th><th>Wartość (PLN)</th><th>m/m-1</th><th>% m/m-1</th>
              <th>r/r-1</th><th>% r/r-1</th><th>Ranking</th><th>Ranking r-1</th><th>Zmiana rankingu</th>
            </tr>
          </thead>
          <tbody>
            {countryData.length > 0 ? countryData.map((row, idx) => (
              <tr key={idx}>
                <td className="bold">{row.kraj}</td>
                <td>{formatNumber(row.current)}</td>
                <td>{formatNumber(row.prev)}</td>
                <td className={`bold ${row.changeMM > 0 ? "positive" : row.changeMM < 0 ? "negative" : ""}`}>
                  {row.changeMM == null ? "—" : `${row.changeMM.toFixed(2)}%`}
                </td>
                <td>{formatNumber(row.yoy)}</td>
                <td className={`bold ${row.changeYY > 0 ? "positive" : row.changeYY < 0 ? "negative" : ""}`}>
                  {row.changeYY == null ? "—" : `${row.changeYY.toFixed(2)}%`}
                </td>
                <td className={
                  row.rank <= 3 ? "highlight-top" :
                  row.rank >= 8 ? "highlight-low" : "highlight-neutral"
                }>{row.rank}</td>
                <td className={
                  row.prevRank <= 3 ? "highlight-top" :
                  row.prevRank >= 8 ? "highlight-low" : "highlight-neutral"
                }>{row.prevRank || "-"}</td>
                <td className={
                  row.rankChange > 0 ? "rank-up" :
                  row.rankChange < 0 ? "rank-down" : "rank-neutral"
                }>
                  {row.rankChange !== null
                    ? row.rankChange > 0 ? `↑ ${row.rankChange}` :
                      row.rankChange < 0 ? `↓ ${Math.abs(row.rankChange)}` : "–"
                    : "-"}
                </td>
              </tr>
            )) : (
              <tr><td colSpan="9" style={{ textAlign: "center" }}>❌ Brak danych dla wybranego okresu</td></tr>
            )}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </Wrapper>
  );
};

export default RevenueByCountry;
